<template>
  <section class="resolve" aria-labelledby="rpd-resolve-heading">
    <div class="resolve__head">
      <h2 id="rpd-resolve-heading">Resolved samples</h2>
      <span class="chip chip--info">
        <i class="material-icons" aria-hidden="true">lock</i>
        One sample per side
      </span>
    </div>

    <p class="resolve__lede">
      What each side's filters matched. Export unlocks only when <em>both</em> sides resolve to
      exactly one distinct <code>ClientSampleID</code>.
    </p>

    <div class="resolve__grid">
      <div class="resolution" :class="primaryState.cls">
        <div class="resolution__head">
          <span class="role-badge role-badge--primary" aria-hidden="true">P</span>
          <span class="resolution__label">Primary</span>
        </div>
        <p class="resolution__value">
          <i class="material-icons" aria-hidden="true">{{ primaryState.icon }}</i>
          {{ primaryState.word }}
        </p>
        <div v-if="primaryIds.length" class="resolution__ids">
          <span v-for="id in primaryIds" :key="id" class="chip chip--mono">{{ id }}</span>
        </div>
        <p class="resolution__meta">{{ primaryMeta }}</p>
      </div>

      <div class="resolution" :class="duplicateState.cls">
        <div class="resolution__head">
          <span class="role-badge role-badge--duplicate" aria-hidden="true">D</span>
          <span class="resolution__label">Duplicate</span>
        </div>
        <p class="resolution__value">
          <i class="material-icons" aria-hidden="true">{{ duplicateState.icon }}</i>
          {{ duplicateState.word }}
        </p>
        <div v-if="duplicateIds.length" class="resolution__ids">
          <span v-for="id in duplicateIds" :key="id" class="chip chip--mono">{{ id }}</span>
        </div>
        <p class="resolution__meta">{{ duplicateMeta }}</p>
      </div>
    </div>

    <!--
      Do the two filter sets actually separate the sides? The counts can pass while both sides
      still describe one sample, so the identity check is stated separately and the block it
      causes is explained rather than left to the reader.
    -->
    <div class="identity">
      <div class="identity__head">
        <i class="material-icons" aria-hidden="true">compare_arrows</i>
        <h3>Do the filters separate the two sides?</h3>
        <span class="chip identity__verdict" :class="identity.collides ? 'chip--error' : 'chip--success'">
          <i class="material-icons" aria-hidden="true">{{ identity.collides ? 'error_outline' : 'check_circle' }}</i>
          {{ identity.collides ? 'Filters describe one sample' : 'Filters differ' }}
        </span>
      </div>

      <div class="identity__row">
        <span class="identity__label">Monitoring location</span>
        <span class="identity__value">{{ identity.locationPrimary }}</span>
        <span class="identity__cmp" aria-hidden="true">{{ identity.locationSame ? '=' : '≠' }}</span>
        <span class="identity__value">{{ identity.locationDuplicate }}</span>
        <span class="chip chip--neutral identity__flag">
          {{ identity.locationSame ? 'Same' : 'Differs' }}
        </span>
      </div>

      <div class="identity__row">
        <span class="identity__label">QA/QC aliases</span>
        <span class="identity__value">{{ identity.aliasPrimary }}</span>
        <span class="identity__cmp" aria-hidden="true">{{ identity.aliasesSame ? '=' : '≠' }}</span>
        <span class="identity__value">{{ identity.aliasDuplicate }}</span>
        <span class="chip chip--neutral identity__flag">
          {{ identity.aliasesSame ? 'Same' : 'Differs' }}
        </span>
      </div>
    </div>

    <!-- The second, live statement of the verdict. The top band is a note, so a change is
         announced once rather than twice. -->
    <RpdVerdictBand :band="band" live />
  </section>
</template>

<script>
  import RpdVerdictBand from './RpdVerdictBand'
  import { resolutionState, resolutionMeta } from '@/helpers/rpd'

  /*
   * The resolved-sample readout and the pairing verdict, read together directly above the export
   * control so the verdict sits at the point of action.
   */
  export default {
    name: 'RpdResolvedSamples',

    components: { RpdVerdictBand },

    props: {
      resolution: {
        type: Object,
        required: true,
      },
      identity: {
        type: Object,
        required: true,
      },
      band: {
        type: Object,
        required: true,
      },
    },

    computed: {
      primaryIds() {
        return this.resolution.primarySampleIds || []
      },

      duplicateIds() {
        return this.resolution.duplicateSampleIds || []
      },

      primaryState() {
        return resolutionState(this.primaryIds)
      },

      duplicateState() {
        return resolutionState(this.duplicateIds)
      },

      /* The degenerate case: both sides landed on one and the same sample. */
      sameSingleSample() {
        return this.primaryIds.length === 1 &&
          this.duplicateIds.length === 1 &&
          this.primaryIds[0] === this.duplicateIds[0]
      },

      primaryMeta() {
        return resolutionMeta(this.primaryIds, false)
      },

      duplicateMeta() {
        return resolutionMeta(this.duplicateIds, this.sameSingleSample)
      },
    },
  }
</script>
